import { useCallback, useMemo, useRef, useState } from 'react'
import { ClickAwayListener, IconButton, Popper, styled } from '@mui/material'
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { InlineTreeSelect } from './InlineTreeSelect'
import {
  CollapsibleOpenState,
  CollapsibleSelectField,
} from './CollapsibleSelectField'
import { useSortableList } from './hooks/useSortableList'
import { SortableChipList } from './SortableChipList'
import { DND_AUTO_SCROLL_CONFIG, DND_MEASURING_CONFIG } from './dndConfig'
import { EntityIcon } from '../../EntityIcon'
import { SelectOption } from '../../../../../model/selectOption'
import { colorPalette } from '../../../../../style/colorPalette'
import { intl } from '../../../../../../i18n'
import { VersionReportVersionPageRowEntity } from '../../../../../../domain/entity/version-report/VersionReportVersionEntity'
import {
  buildTree,
  TreeSelectOption,
} from '../../../../../features/ProcessScopeSearchSelectPopper/treeUtils'

type Props = {
  options: SelectOption[]
  parentMap: Record<string, string>
  value: string[]
  onChange: (value: string[]) => void
  summaryLabel: string
  multiSelect?: boolean
  height?: string
  loading?: boolean
  placeholder?: string
  rows?: VersionReportVersionPageRowEntity[]
  collapsible?: boolean
  openState?: CollapsibleOpenState
  iconVariant?: 'circular' | 'square'
}

export const TreeSelectWithSummary = ({
  options,
  parentMap,
  value,
  onChange,
  summaryLabel,
  multiSelect = true,
  height,
  loading = false,
  placeholder,
  rows,
  collapsible = false,
  openState,
  iconVariant = 'square',
}: Props) => {
  const [summaryOpen, setSummaryOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const reorderable = rows !== undefined

  const optionsByValue = useMemo(
    () => new Map(options.map(option => [option.value, option])),
    [options]
  )

  const treeOrderIndexByUuid = useMemo(() => {
    const tree = buildTree(options, parentMap)
    const flatten = (nodes: TreeSelectOption[]): string[] =>
      nodes.flatMap(node => [node.value, ...flatten(node.children)])
    return new Map(flatten(tree).map((value, index) => [value, index]))
  }, [options, parentMap])

  const selectedItems = useMemo(() => {
    const items = value
      .map(itemValue => optionsByValue.get(itemValue))
      .filter((option): option is SelectOption => Boolean(option))

    if (reorderable) return items

    return [...items].sort((a, b) => {
      const indexA = treeOrderIndexByUuid.get(a.value) ?? 0
      const indexB = treeOrderIndexByUuid.get(b.value) ?? 0
      return indexA - indexB
    })
  }, [value, optionsByValue, reorderable, treeOrderIndexByUuid])

  const handleToggleOpen = useCallback(() => setSummaryOpen(open => !open), [])
  const handleClose = useCallback(() => setSummaryOpen(false), [])

  const handleRemove = useCallback(
    (itemValue: string) => {
      onChange(value.filter(v => v !== itemValue))
    },
    [value, onChange]
  )

  const handleReorder = useCallback(
    (reorderedItems: SelectOption[]) => {
      onChange(reorderedItems.map(item => item.value))
    },
    [onChange]
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  )

  const { handleDragStart, handleDragEnd, handleDragCancel } = useSortableList({
    items: selectedItems,
    onReorder: handleReorder,
    getId: item => item.value,
  })

  if (collapsible) {
    return (
      <CollapsibleSelectField
        chips={selectedItems.map(item => ({
          key: item.value,
          label: item.name,
          iconUrl: item.iconUrl,
        }))}
        emptyLabel={intl.formatMessage({
          id: 'versionReport.treeSelect.empty',
        })}
        openState={openState}
        renderChips={
          reorderable
            ? chips => (
                <SortableChipList
                  chips={chips}
                  onReorder={onChange}
                  onRemove={handleRemove}
                />
              )
            : undefined
        }
      >
        <InlineTreeSelect
          options={options}
          parentMap={parentMap}
          value={value}
          onChange={onChange}
          multiSelect={multiSelect}
          height={height}
          loading={loading}
          placeholder={placeholder}
          iconVariant={iconVariant}
        />
      </CollapsibleSelectField>
    )
  }

  return (
    <Container>
      <ToggleRow type="button" ref={anchorRef} onClick={handleToggleOpen}>
        <ToggleLabel>
          {summaryLabel}
          <CountLabel>
            {selectedItems.length}
            {intl.formatMessage({ id: 'versionReport.treeSelect.count' })}
          </CountLabel>
        </ToggleLabel>
        {summaryOpen ? (
          <ExpandLessRounded sx={{ color: colorPalette.monotone[4] }} />
        ) : (
          <ExpandMoreRounded sx={{ color: colorPalette.monotone[4] }} />
        )}
      </ToggleRow>

      <Popper
        open={summaryOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        disablePortal={true}
        sx={{ zIndex: 1301, width: anchorRef.current?.offsetWidth }}
        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <PopperPaper>
            {selectedItems.length === 0 ? (
              <EmptyState>
                {intl.formatMessage({ id: 'versionReport.treeSelect.empty' })}
              </EmptyState>
            ) : reorderable ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                measuring={DND_MEASURING_CONFIG}
                autoScroll={DND_AUTO_SCROLL_CONFIG}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
              >
                <SortableContext
                  items={selectedItems.map(item => item.value)}
                  strategy={verticalListSortingStrategy}
                >
                  <List>
                    {selectedItems.map((item, index) => (
                      <SortableRow
                        key={item.value}
                        index={index}
                        item={item}
                        onRemove={handleRemove}
                        iconVariant={iconVariant}
                      />
                    ))}
                  </List>
                </SortableContext>
              </DndContext>
            ) : (
              <List>
                {selectedItems.map((item, index) => (
                  <Row
                    key={item.value}
                    index={index}
                    item={item}
                    onRemove={handleRemove}
                    iconVariant={iconVariant}
                  />
                ))}
              </List>
            )}
          </PopperPaper>
        </ClickAwayListener>
      </Popper>

      <InlineTreeSelect
        options={options}
        parentMap={parentMap}
        value={value}
        onChange={onChange}
        multiSelect={multiSelect}
        height={height}
        loading={loading}
        placeholder={placeholder}
        iconVariant={iconVariant}
      />
    </Container>
  )
}

type RowProps = {
  index: number
  item: SelectOption
  onRemove: (value: string) => void
  iconVariant: 'circular' | 'square'
}

const Row = ({ index, item, onRemove, iconVariant }: RowProps) => (
  <RowContainer>
    <RowIndex>{index + 1}</RowIndex>
    <EntityIcon src={item.iconUrl} size={16} variant={iconVariant} />
    <RowName>{item.name}</RowName>
    <RemoveButton
      onClick={() => onRemove(item.value)}
      aria-label={intl.formatMessage({ id: 'versionReport.treeSelect.remove' })}
      size="small"
    >
      <CloseRoundedIcon fontSize="small" />
    </RemoveButton>
  </RowContainer>
)

const SortableRow = ({ index, item, onRemove, iconVariant }: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.value })

  const style = useMemo(
    () => ({ transform: CSS.Transform.toString(transform), transition }),
    [transform, transition]
  )

  return (
    <RowContainer ref={setNodeRef} style={style} isDragging={isDragging}>
      <DragHandle
        type="button"
        {...attributes}
        {...listeners}
        aria-label={intl.formatMessage({
          id: 'versionReport.treeSelect.dragHandle',
        })}
      >
        <DragIndicatorRoundedIcon fontSize="small" />
      </DragHandle>
      <RowIndex>{index + 1}</RowIndex>
      <EntityIcon src={item.iconUrl} size={16} variant={iconVariant} />
      <RowName>{item.name}</RowName>
      <RemoveButton
        onClick={() => onRemove(item.value)}
        aria-label={intl.formatMessage({
          id: 'versionReport.treeSelect.remove',
        })}
        size="small"
      >
        <CloseRoundedIcon fontSize="small" />
      </RemoveButton>
    </RowContainer>
  )
}

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  width: '100%',
})

const ToggleRow = styled('button')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  padding: '6px 10px',
  background: colorPalette.monotone[0],
  cursor: 'pointer',

  '&:hover': {
    background: colorPalette.monotone[1],
  },
})

const ToggleLabel = styled('span')({
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  fontSize: '13px',
  fontWeight: 'bold',
  color: colorPalette.monotone[10],
})

const CountLabel = styled('span')({
  fontSize: '12px',
  fontWeight: 'normal',
  color: colorPalette.monotone[4],
})

const PopperPaper = styled('div')({
  background: colorPalette.monotone[0],
  boxShadow:
    'rgb(0 0 0 / 20%) 0px 5px 5px -3px, rgb(0 0 0 / 14%) 0px 8px 10px 1px, rgb(0 0 0 / 12%) 0px 3px 14px 2px',
  borderRadius: '4px',
  padding: '10px',
  maxHeight: '260px',
  overflowY: 'auto',
  overflowAnchor: 'none',
})

const EmptyState = styled('div')({
  fontSize: '12px',
  color: colorPalette.monotone[4],
  padding: '10px 4px',
  textAlign: 'center',
})

const List = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
})

const RowIndex = styled('span')({
  fontSize: '12px',
  color: colorPalette.monotone[4],
  minWidth: '18px',
  textAlign: 'center',
  flexShrink: 0,
})

const RowContainer = styled('div', {
  shouldForwardProp: prop => prop !== 'isDragging',
})<{ isDragging?: boolean }>(({ isDragging }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 6px',
  borderRadius: '4px',
  background: colorPalette.monotone[0],
  opacity: isDragging ? 0.5 : 1,
  zIndex: isDragging ? 1 : 'auto',
  position: 'relative',
  '&:hover': {
    background: colorPalette.monotone[1],
  },
}))

const DragHandle = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '20px',
  height: '20px',
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: colorPalette.monotone[4],
  cursor: 'grab',
  touchAction: 'none',
  '&:hover': {
    color: colorPalette.monotone[10],
  },
})

const RowName = styled('span')({
  flex: 1,
  minWidth: 0,
  fontSize: '13px',
  color: colorPalette.monotone[10],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const RemoveButton = styled(IconButton)({
  padding: '4px',
  color: colorPalette.monotone[4],
  '&:hover': {
    color: colorPalette.monotone[10],
    background: colorPalette.monotone[2],
  },
})
